import { useFocusRing } from '@react-native-aria/focus'
import clsx from 'clsx'
import type { ReactNode } from 'react'
import { Pressable } from 'react-native'

import { Avatar } from '@/src/elements/avatar'
import { chipVariants } from '@/src/elements/chip/chip-variants'
import { Typography } from '@/src/elements/text/Typography'
import { getAvatarUrl } from '@/src/general/utils/getAvatarUrl'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TCommunityApiRoleInfos, TMediumCommunityApi } from '../../api/communityApi.types'

type TCommunityUserChipProps = {
  onPress?: VoidFunction
  className?: string
  community: Partial<TCommunityApiRoleInfos> & Partial<Pick<TMediumCommunityApi, 'user'>>
}

export const CommunityUserChip = ({
  onPress,
  className,
  community: { isAdmin, isAnimator, isCreator, user },
}: TCommunityUserChipProps): ReactNode => {
  const { t } = useTranslate()
  const { isFocusVisible, focusProps } = useFocusRing()

  const hasRole = isAdmin || isAnimator || isCreator

  let text: string | undefined

  if (isCreator) {
    text = t('community.role.creator.long')
  } else if (isAdmin) {
    text = t('community.role.administrator.long')
  } else if (isAnimator) {
    text = t('community.role.animator.long')
  } else if (user) {
    text = t('words.by-user', { userName: user.name })
  }

  const slots = chipVariants({
    colors: hasRole ? 'success' : 'default',
    variant: 'flat',
    hasText: !!text,
    isPressable: !!onPress,
  })

  return (
    <Pressable
      className={slots.background({
        className: clsx(
          'h-10 px-2 py-0.5 max-w-full',
          !hasRole && 'bg-transparent',
          !text && 'w-36 animate-pulse',
          className,
        ),
      })}
      onPress={onPress}
      style={{ maxHeight: 42 }}
      tabIndex={!onPress ? -1 : 0}
      dataSet={{
        'focus-visible': isFocusVisible,
        ...(!hasRole && user ? { 'visual-test': 'transparent' } : {}),
      }}
      {...focusProps}
    >
      {!!user?.avatar && <Avatar src={getAvatarUrl(user.avatar)} size="xs" />}
      {!!text && (
        <Typography numberOfLines={1} size="sm" className={clsx(slots.text(), slots.textColor())}>
          {text}
        </Typography>
      )}
    </Pressable>
  )
}
